import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Signup() {
  const { signup, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await signup(name.trim(), email.trim().toLowerCase(), password);
      if (res.token) {
        navigate("/");
      } else {
        toast.success("Account created", { description: "Your account may need admin approval before access." });
        navigate("/login");
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-curtron-bg p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-10 h-10 rounded-md bg-curtron-green flex items-center justify-center text-white font-heading font-bold text-lg">C</div>
          <div className="font-heading font-bold text-curtron-ink text-lg">Curtron Hub</div>
        </div>
        <h1 className="font-heading text-2xl font-bold text-curtron-ink tracking-tight">Create account</h1>
        <p className="text-sm text-curtron-sub mt-1 mb-6">Request access to the transport hub.</p>

        <form onSubmit={submit} className="space-y-4">
          {[
            { l: "Full name", v: name, set: setName, t: "text", tid: "signup-name-input", ph: "Jane Doe" },
            { l: "Email", v: email, set: setEmail, t: "email", tid: "signup-email-input", ph: "you@curtron.com" },
            { l: "Password", v: password, set: setPassword, t: "password", tid: "signup-password-input", ph: "••••••••" },
          ].map((f) => (
            <div key={f.l}>
              <label className="text-xs uppercase tracking-[0.05em] font-medium text-curtron-sub">{f.l}</label>
              <input
                type={f.t} required value={f.v} onChange={(e) => f.set(e.target.value)} data-testid={f.tid} placeholder={f.ph}
                className="mt-1.5 w-full h-11 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green focus:ring-2 focus:ring-curtron-green/20 transition"
              />
            </div>
          ))}
          {error && <div data-testid="signup-error" className="text-sm text-curtron-terracotta bg-curtron-terracotta/10 rounded-md px-3 py-2">{error}</div>}
          <button type="submit" disabled={loading} data-testid="signup-submit-btn"
            className="w-full h-11 rounded-md bg-curtron-green text-white text-sm font-medium hover:bg-curtron-greenHover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
          </button>
        </form>
        <p className="text-sm text-curtron-sub mt-6">
          Already have an account?{" "}
          <Link to="/login" data-testid="goto-login-link" className="text-curtron-green font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
